import React, { useCallback, useEffect, useState } from "react";
import {
    Loader2, Activity, Clock, CheckCircle2, AlertTriangle, RefreshCw, Users, Sparkles, Send, DollarSign, TrendingUp, Info, Video,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const BUCKET_LABELS = {
    pending_requests: "Pending Requests",
    awaiting_consent: "Awaiting Consent",
    ready_for_checkr: "Ready for Checkr",
    in_progress: "In Progress",
    completed: "Completed",
    review_required: "Review Required",
    declined: "Declined",
    expired: "Expired",
};
const BUCKET_TONE = {
    pending_requests: "slate",
    awaiting_consent: "amber",
    ready_for_checkr: "sky",
    in_progress: "violet",
    completed: "emerald",
    review_required: "amber",
    declined: "rose",
    expired: "muted",
};
const TONE_CLASS = {
    slate: "bg-slate-500/10 text-slate-700 dark:text-slate-300",
    amber: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    sky: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
    violet: "bg-violet-500/15 text-violet-700 dark:text-violet-400",
    emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    rose: "bg-rose-500/15 text-rose-700 dark:text-rose-400",
    muted: "bg-muted text-muted-foreground",
};

const Kpi = ({ icon: Icon, label, value, sub, testId, tone = "emerald" }) => (
    <div className={`rounded-2xl border border-border bg-card p-4 ${TONE_CLASS[tone] ? "" : ""}`} data-testid={testId}>
        <div className={`inline-flex items-center gap-1.5 text-xs font-semibold ${TONE_CLASS[tone]?.split(" ").slice(-1)[0]}`}>
            <Icon size={12} /> {label}
        </div>
        <div className="mt-2 text-3xl font-heading font-semibold text-foreground">{value ?? "—"}</div>
        {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
);

const VerificationOpsDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pinging, setPinging] = useState(false);
    const [pingResult, setPingResult] = useState(null);
    // Unit-economics tile — separate loader so a slow aggregation
    // doesn't hold up the main KPI paint.
    const [econ, setEcon] = useState(null);
    const [econLoading, setEconLoading] = useState(true);
    // Video intro analytics — separate loader for the same reason.
    const [vidStats, setVidStats] = useState(null);
    const [vidLoading, setVidLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/verifications/ops-dashboard");
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    const loadEcon = useCallback(async () => {
        setEconLoading(true);
        try {
            const { data } = await api.get("/admin/verifications/unit-economics?months=6");
            setEcon(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setEconLoading(false); }
    }, []);
    const loadVideoStats = useCallback(async () => {
        setVidLoading(true);
        try {
            const { data } = await api.get("/admin/video-intro/analytics?days=30");
            setVidStats(data);
        } catch (e) {
            // Silent — this tile is bonus, don't block the dashboard on it.
            console.warn("[VerificationOps] video-intro analytics failed", e?.message);
        }
        finally { setVidLoading(false); }
    }, []);
    useEffect(() => { load(); loadEcon(); loadVideoStats(); }, [load, loadEcon, loadVideoStats]);

    const sendDiscordPing = async () => {
        setPinging(true);
        setPingResult(null);
        try {
            const { data } = await api.post("/admin/discord/test-ping");
            setPingResult(data);
            if (data.ok) toast.success("Discord ping delivered — check your channel.");
            else toast.error(data.message || "Discord ping failed.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPinging(false); }
    };

    if (loading || !data) return <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>;

    const { kpis, buckets, top_employers_by_requests: topEmployers } = data;

    return (
        <div data-testid="verification-ops-dashboard" className="space-y-6">
            <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                    <p className="overline">Command Center</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Verification Ops</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Manual Checkr workflow snapshot — assignment, turnaround, and expiry health. All metrics stay valid once the API integration lands.
                    </p>
                </div>
                <button onClick={() => { load(); loadEcon(); loadVideoStats(); }} disabled={loading} data-testid="vops-refresh" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted">
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {/* Discord webhook validator */}
            <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid="vops-discord-card">
                <div className="min-w-0">
                    <div className="text-xs font-semibold text-foreground inline-flex items-center gap-1.5">
                        <Send size={12} className="text-indigo-500" /> Discord alerts webhook
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                        Sends a sanity ping to <code className="font-mono">DISCORD_WEBHOOK_URL</code>. Use this the moment you paste a new webhook in <code className="font-mono">backend/.env</code>.
                    </div>
                    {pingResult && (
                        <div
                            data-testid="vops-discord-result"
                            className={`mt-2 text-xs font-medium ${pingResult.ok ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}`}
                        >
                            {pingResult.ok ? "✓ " : "⚠ "}{pingResult.message}
                            {pingResult.status_code != null && ` (HTTP ${pingResult.status_code})`}
                        </div>
                    )}
                </div>
                <button
                    onClick={sendDiscordPing}
                    disabled={pinging}
                    data-testid="vops-discord-ping"
                    className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90 disabled:opacity-60"
                >
                    {pinging ? <><Loader2 size={12} className="animate-spin" /> Sending…</> : <><Send size={12} /> Send test ping</>}
                </button>
            </div>

            {/* Top-line KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi testId="kpi-pending" icon={Clock} label="Pending verifications" value={kpis.pending_verifications} sub="All non-terminal statuses" tone="amber" />
                <Kpi testId="kpi-avg-turnaround" icon={Activity} label="Avg turnaround" value={kpis.avg_turnaround_hours !== null ? `${kpis.avg_turnaround_hours}h` : "—"} sub="90-day rolling window" tone="violet" />
                <Kpi testId="kpi-week-completed" icon={CheckCircle2} label="Completed this week" value={kpis.completed_this_week} sub="Rolling 7 days" tone="emerald" />
                <Kpi testId="kpi-expiring" icon={AlertTriangle} label="Expiring in 30d" value={kpis.expiring_in_30_days} sub="Requires renewal" tone={kpis.expiring_in_30_days ? "rose" : "muted"} />
            </div>

            {/* Buckets */}
            <div>
                <h2 className="font-heading font-semibold text-lg text-foreground mb-3 inline-flex items-center gap-2">
                    <Sparkles size={14} className="text-emerald-500" /> Candidate requests by status
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vops-buckets">
                    {Object.keys(BUCKET_LABELS).map((k) => (
                        <div key={k} data-testid={`bucket-${k}`} className={`rounded-xl border border-border p-4 ${TONE_CLASS[BUCKET_TONE[k]]}`}>
                            <div className="text-[10px] uppercase tracking-wider font-bold">{BUCKET_LABELS[k]}</div>
                            <div className="mt-1 text-2xl font-heading font-semibold text-foreground">{buckets[k] || 0}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Unit Economics — per-SKU per-month gross margin. See
                routers/verification_dashboard.unit_economics for the math.
                Provider unit costs are env-overridable — override
                CHECKR_COST_* / PROVIDER_COST_* once Checkr sends contract rates. */}
            <div data-testid="vops-unit-economics">
                <h2 className="font-heading font-semibold text-lg text-foreground mb-3 inline-flex items-center gap-2">
                    <DollarSign size={14} className="text-emerald-500" /> Simonara Verify™ unit economics
                    <span className="text-xs font-normal text-muted-foreground">· last 6 months</span>
                </h2>
                {econLoading || !econ ? (
                    <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
                        <Loader2 size={16} className="animate-spin inline mr-2" /> Crunching margins…
                    </div>
                ) : (
                    <>
                        {/* Top-line totals */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                            <Kpi
                                testId="econ-revenue" icon={DollarSign} label="Paid revenue"
                                value={`$${econ.totals.revenue.toFixed(2)}`}
                                sub={`${econ.totals.paid_count} paid check${econ.totals.paid_count === 1 ? "" : "s"}`}
                                tone="emerald"
                            />
                            <Kpi
                                testId="econ-net" icon={TrendingUp}
                                label="Net after costs" value={`$${econ.totals.net.toFixed(2)}`}
                                sub={`Margin ${econ.totals.margin_pct != null ? econ.totals.margin_pct + "%" : "—"}`}
                                tone={econ.totals.margin_pct != null && econ.totals.margin_pct >= 30 ? "emerald"
                                    : econ.totals.margin_pct != null && econ.totals.margin_pct >= 0 ? "amber" : "rose"}
                            />
                            <Kpi
                                testId="econ-provider-cost" icon={Activity}
                                label="Provider cost (paid)"
                                value={`$${econ.totals.provider_cost_paid.toFixed(2)}`}
                                sub={`+ $${econ.totals.stripe_fees.toFixed(2)} Stripe fees`}
                                tone="violet"
                            />
                            <Kpi
                                testId="econ-promo-spend" icon={Sparkles}
                                label="Promo spend (free tier)"
                                value={`$${econ.totals.promo_spend.toFixed(2)}`}
                                sub={`${econ.totals.free_tier_count} $1.50 Stripe Identity absorbed`}
                                tone="slate"
                            />
                        </div>

                        {/* Per-SKU per-month table */}
                        {econ.per_bucket.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-border bg-card/60 p-8 text-center text-sm text-muted-foreground" data-testid="econ-empty">
                                No verification revenue yet. Once your first paid check lands, per-SKU margins land here.
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                                <table className="w-full text-sm" data-testid="econ-table">
                                    <thead className="bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                        <tr>
                                            <th className="px-4 py-3 text-left">Month</th>
                                            <th className="px-4 py-3 text-left">SKU</th>
                                            <th className="px-4 py-3 text-right">Paid</th>
                                            <th className="px-4 py-3 text-right">Revenue</th>
                                            <th className="px-4 py-3 text-right">Provider</th>
                                            <th className="px-4 py-3 text-right">Stripe fees</th>
                                            <th className="px-4 py-3 text-right">Net</th>
                                            <th className="px-4 py-3 text-right">Margin</th>
                                            <th className="px-4 py-3 text-right" title="Free-tier count + promo spend (Simonara absorbed $1.50 Stripe Identity)">Promo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {econ.per_bucket.map((r, i) => {
                                            const marginTone = r.margin_pct == null ? "text-muted-foreground"
                                                : r.margin_pct >= 30 ? "text-emerald-600 dark:text-emerald-400 font-semibold"
                                                : r.margin_pct >= 0 ? "text-amber-600 dark:text-amber-400 font-semibold"
                                                : "text-rose-600 dark:text-rose-400 font-semibold";
                                            return (
                                                <tr key={i} className="border-t border-border" data-testid={`econ-row-${r.month}-${r.type}`}>
                                                    <td className="px-4 py-3 text-foreground font-mono text-xs">{r.month}</td>
                                                    <td className="px-4 py-3 text-foreground capitalize">{r.type}</td>
                                                    <td className="px-4 py-3 text-right font-mono">{r.paid_count}</td>
                                                    <td className="px-4 py-3 text-right font-mono">${r.revenue.toFixed(2)}</td>
                                                    <td className="px-4 py-3 text-right font-mono text-muted-foreground">${r.provider_cost_paid.toFixed(2)}</td>
                                                    <td className="px-4 py-3 text-right font-mono text-muted-foreground">${r.stripe_fees.toFixed(2)}</td>
                                                    <td className="px-4 py-3 text-right font-mono">${r.net.toFixed(2)}</td>
                                                    <td className={`px-4 py-3 text-right font-mono ${marginTone}`}>
                                                        {r.margin_pct != null ? `${r.margin_pct}%` : "—"}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-mono text-slate-500">
                                                        {r.free_tier_count > 0 ? `${r.free_tier_count} · $${r.promo_spend.toFixed(2)}` : "—"}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Assumption footnote — critical because these numbers
                            drive real pricing decisions. */}
                        <div className="mt-3 rounded-xl border border-border bg-muted/20 p-3 text-xs text-muted-foreground flex items-start gap-2" data-testid="econ-assumptions">
                            <Info size={13} className="mt-0.5 shrink-0 text-sky-500" />
                            <div>
                                <div className="text-foreground font-semibold mb-1">Assumed provider unit costs</div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1">
                                    {Object.entries(econ.provider_cost_defaults).map(([vtype, providers]) => (
                                        <span key={vtype} className="capitalize">
                                            {vtype}: {Object.entries(providers).map(([p, c]) => `${p} $${c.toFixed(2)}`).join(" · ")}
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-2">
                                    Stripe fees: {(econ.stripe_fee_config.pct * 100).toFixed(1)}% + ${econ.stripe_fee_config.fixed.toFixed(2)} per paid charge.
                                    Override via <code className="font-mono">CHECKR_COST_*</code> / <code className="font-mono">PROVIDER_COST_*</code> env vars once Checkr sends contract rates.
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Video Intro analytics — record vs skip funnel over the last
                30 days. Silent-fail: if the analytics endpoint 404s or
                errors we just hide the tile rather than blocking the whole
                dashboard. */}
            {vidStats && (
                <div data-testid="vops-video-intro-analytics">
                    <h2 className="font-heading font-semibold text-lg text-foreground mb-3 inline-flex items-center gap-2">
                        <Video size={14} className="text-emerald-500" /> Video intro funnel
                        <span className="text-xs font-normal text-muted-foreground">· last {vidStats.days} days</span>
                    </h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                        <Kpi testId="vid-recorded" icon={CheckCircle2} label="Recorded"
                             value={vidStats.recorded_count}
                             sub={vidStats.record_rate_pct != null ? `${vidStats.record_rate_pct}% record rate` : "no signal yet"}
                             tone={vidStats.record_rate_pct != null && vidStats.record_rate_pct >= 40 ? "emerald"
                                 : vidStats.record_rate_pct != null && vidStats.record_rate_pct >= 20 ? "amber" : "slate"}
                        />
                        <Kpi testId="vid-skipped" icon={Clock} label="Skipped"
                             value={vidStats.skipped_count}
                             sub={`"I'll decide later" clicks`}
                             tone="slate"
                        />
                        <Kpi testId="vid-started-no-record" icon={AlertTriangle} label="Started but bailed"
                             value={vidStats.started_but_not_recorded_count}
                             sub="Granted camera, didn't upload"
                             tone={vidStats.started_but_not_recorded_count > vidStats.recorded_count ? "rose" : "amber"}
                        />
                        <Kpi testId="vid-live" icon={Sparkles} label="Live intros right now"
                             value={vidStats.current_live}
                             sub={`avg ${vidStats.avg_size_mb}MB per video`}
                             tone="violet"
                        />
                    </div>
                    {vidStats.series.length > 0 && (
                        <div className="rounded-2xl border border-border bg-card p-4" data-testid="vid-series">
                            <div className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Daily activity</div>
                            <div className="flex items-end gap-1 h-24 overflow-x-auto">
                                {vidStats.series.map((d) => {
                                    const total = d.recorded + d.skipped;
                                    const maxSeries = Math.max(...vidStats.series.map(x => x.recorded + x.skipped), 1);
                                    const rh = Math.round((d.recorded / maxSeries) * 80);
                                    const sh = Math.round((d.skipped / maxSeries) * 80);
                                    return (
                                        <div key={d.date} className="shrink-0 flex flex-col items-center gap-1"
                                             title={`${d.date}: ${d.recorded} recorded · ${d.skipped} skipped`}
                                             data-testid={`vid-series-${d.date}`}>
                                            <div className="flex flex-col justify-end gap-0.5" style={{ height: 80 }}>
                                                <div className="w-3 bg-slate-400/50 rounded-sm" style={{ height: sh }} />
                                                <div className="w-3 bg-emerald-500 rounded-sm" style={{ height: rh }} />
                                            </div>
                                            <div className="text-[9px] font-mono text-muted-foreground">
                                                {d.date.slice(5)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-3 flex items-center gap-4 text-[11px] text-muted-foreground">
                                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" /> Recorded</span>
                                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-slate-400/50 rounded-sm" /> Skipped</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {vidLoading && !vidStats && (
                <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
                    <Loader2 size={16} className="animate-spin inline mr-2" /> Loading video-intro analytics…
                </div>
            )}



            {/* Top employers */}
            <div>
                <h2 className="font-heading font-semibold text-lg text-foreground mb-3 inline-flex items-center gap-2">
                    <Users size={14} className="text-emerald-500" /> Employer requests by company
                </h2>
                {(topEmployers || []).length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border bg-card/60 p-8 text-center text-sm text-muted-foreground" data-testid="vops-employers-empty">
                        No employer requests yet.
                    </div>
                ) : (
                    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="vops-employers">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 text-left">Company</th>
                                    <th className="px-4 py-3 text-right">Requests</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topEmployers.map((e, i) => (
                                    <tr key={i} className="border-t border-border">
                                        <td className="px-4 py-3 text-foreground">{e.company}</td>
                                        <td className="px-4 py-3 text-right font-semibold">{e.requests}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VerificationOpsDashboard;
